import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { MonitoringEventTypeType } from "../../shared/types/monitoring";
import { useAv, useAvPubsubMessaging } from "../../utils/av";

import { BaseFooter } from "./BaseFooter";
import { JoinInPersonClassButton } from "./JoinInPersonClassButton";
import LeaveClassButton from "./LeaveClassButton";
import { MobilePresentationBanner } from "./MobilePresentationBanner";
import { TimerBanner } from "./TimerBanner";
import { IconButtons } from "./icon-buttons/IconButtons";

import { withActiveAv } from "components/av/AvSubsystemLoader";
import { LargeClassHandControl } from "components/av/LargeClassHandControl";
import { ScreenshareOptions } from "components/av/ScreenshareOptions";
import { useVirtualClassActions } from "components/av/virtual-class-utils";
import { PillBanner } from "components/footer/PillBanner";
import { PillBannerButton } from "components/footer/PillBannerButton";
import { ReactionFeed } from "components/footer/ReactionFeed";
import { StudentPreviewOptions } from "components/footer/StudentPreviewOptions";
import { PresentationOptions } from "components/materials/presentation/PresentationOptions";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { VideoIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  selectIsClassSessionInPerson,
  selectIsPracticeClassSession,
  selectIsStudentPreview,
} from "store/selectors";
import { selectIsInBreakout } from "store/selectors/breakouts";
import {
  selectCurrentClassUser,
  selectCurrentClassUserIsInPersonClass,
} from "store/selectors/class-user";
import { selectScreensharingClassUserId } from "store/selectors/virtual-classes";
import { useCreateMonitoringEvent } from "utils/hooks/use-create-monitoring-event";
import { usePresentation } from "utils/presentation";

const LazyVideoMicControls = withActiveAv(() => import("components/av/VideoMicControls"));
const LazyBreakoutsBanner = withActiveAv(
  () => import("components/footer/breakouts-banner/BreakoutsBanner"),
);

const ANIMATION_DURATION = 500;

export const StudentMainFooter = () => {
  const isStudentPreview = useAppSelector(selectIsStudentPreview);
  const screensharingClassUserId = useAppSelector(selectScreensharingClassUserId);
  const currentClassUser = useAppSelector(selectCurrentClassUser);
  const { presentedPageId } = usePresentation();
  const isBreakoutActive = useAppSelector(selectIsInBreakout);
  const isClassSessionInPerson = useAppSelector(selectIsClassSessionInPerson);
  const { isInVirtualClass, isAvActiveInAnotherTab, isVirtualClassActive, isAvSupportedInBrowser } =
    useAv();
  const isInInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);
  const isInClassSession = isInVirtualClass || isInInPersonClass;
  const isPracticeClassSession = useAppSelector(selectIsPracticeClassSession);
  const isInBreakoutRoom =
    isBreakoutActive && isInVirtualClass && !!currentClassUser?.breakout_group_id;
  const isMobile = !useIsMdOrLarger();
  const showPresentationOrScreenshareBanner =
    isInClassSession && (screensharingClassUserId || !isInBreakoutRoom);

  // Animation state for presentation/screenshare banner reveal/hide and content changes
  // Enter: offset (instant, w-0) → animating (transition to measured width) → idle
  // Exit: exitOffset (instant, at width) → exitAnimating (transition to 0) → idle
  const prevPresentedPageId = useRef<string | null>(null);
  const prevScreensharingClassUserId = useRef<string | null>(null);
  type AnimationPhase = "idle" | "offset" | "animating" | "exitOffset" | "exitAnimating";
  const [animationPhase, setAnimationPhase] = useState<AnimationPhase>("idle");
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const bannerContentRef = useRef<HTMLDivElement | null>(null);
  const [bannerWidth, setBannerWidth] = useState<number | null>(null);

  // Use useLayoutEffect to set initial state BEFORE browser paints
  useLayoutEffect(() => {
    const wasBannerVisible =
      !!prevPresentedPageId.current || !!prevScreensharingClassUserId.current;
    const isBannerVisible = !!presentedPageId || !!screensharingClassUserId;

    // Entering: banner not visible → banner visible (while in class)
    const isEntering = isInClassSession && !wasBannerVisible && isBannerVisible;

    if (isEntering) {
      // Clear any existing animation before starting new one
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);

      // Measure the banner content width before animating
      if (bannerContentRef.current) {
        setBannerWidth(bannerContentRef.current.scrollWidth);
      }

      // Phase 1: Apply left offset instantly (buttons stay at original position)
      setAnimationPhase("offset");

      // Phase 2: Next frame - remove offset with transition → buttons animate right
      requestAnimationFrame(() => {
        setAnimationPhase("animating");
      });

      // Phase 3: After animation completes, show banner
      animationTimeoutRef.current = setTimeout(() => {
        setAnimationPhase("idle");
        animationTimeoutRef.current = null;
      }, ANIMATION_DURATION);

      // Exiting: banner visible → banner not visible (while in class)
    } else if (isInClassSession && wasBannerVisible && !isBannerVisible) {
      // Clear any existing animation before starting new one
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);

      // Measure current width before exit animation
      if (bannerContentRef.current) {
        setBannerWidth(bannerContentRef.current.scrollWidth);
      }

      // Phase 1: Apply right offset instantly (buttons stays in place despite banner disappearing)
      setAnimationPhase("exitOffset");

      // Phase 2: Next frame - remove offset with transition → buttons animate left
      requestAnimationFrame(() => {
        setAnimationPhase("exitAnimating");
      });

      // After animation completes, return to idle
      animationTimeoutRef.current = setTimeout(() => {
        setAnimationPhase("idle");
        animationTimeoutRef.current = null;
      }, ANIMATION_DURATION);
    }

    // Safety: if we're rendering the animated banner but stuck in a non-idle state
    // without an active animation timeout, reset to idle to ensure visibility
    const shouldShowAnimatedBanner =
      screensharingClassUserId || (isInClassSession && !isInBreakoutRoom);
    if (shouldShowAnimatedBanner && animationPhase !== "idle" && !animationTimeoutRef.current) {
      setAnimationPhase("idle");
    }

    prevPresentedPageId.current = presentedPageId ?? null;
    prevScreensharingClassUserId.current = screensharingClassUserId ?? null;
  }, [
    presentedPageId,
    screensharingClassUserId,
    isInClassSession,
    isInBreakoutRoom,
    animationPhase,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
    };
  }, []);

  const createMonitoringEvent = useCreateMonitoringEvent();
  useEffect(() => {
    if (isVirtualClassActive && isAvActiveInAnotherTab) {
      createMonitoringEvent(MonitoringEventTypeType.AV_PRESENT_IN_ANOTHER_TAB, {});
    }
  }, [isVirtualClassActive, isAvActiveInAnotherTab, createMonitoringEvent]);

  const { sendPubsubVirtualClassStateMessage } = useAvPubsubMessaging();

  const { showAvCheckToJoinVirtualClass } = useVirtualClassActions();

  const onJoinVirtualClass = useCallback(() => {
    showAvCheckToJoinVirtualClass();
  }, [showAvCheckToJoinVirtualClass]);

  const onMoveVirtualClassToThisTab = useCallback(() => {
    sendPubsubVirtualClassStateMessage({
      action: "join",
      force: true,
    });
  }, [sendPubsubVirtualClassStateMessage]);

  const MaybeJoinVirtualClassButton =
    isVirtualClassActive &&
    !isPracticeClassSession &&
    !isInVirtualClass &&
    isAvSupportedInBrowser ? (
      <PillBanner
        buttonChildren={
          isAvActiveInAnotherTab ? (
            <PillBannerButton
              eventName="Move Virtual Class to This Tab"
              kind="blue"
              title={t("av.move_virtual_class_to_this_tab")}
              onClick={onMoveVirtualClassToThisTab}
            >
              {t("av.move_virtual_class_to_this_tab")}
            </PillBannerButton>
          ) : (
            <PillBannerButton
              eventName="Enter class"
              kind="blue"
              title={t("footer.join_session")}
              onClick={onJoinVirtualClass}
            >
              {t("footer.join_session")}
            </PillBannerButton>
          )
        }
        kind="blue"
        textChildren={
          <IconText
            iconStart={<VideoIcon className="text-blue-shade-40" />}
            text={
              isAvActiveInAnotherTab
                ? t("av.virtual_class_in_another_tab")
                : t("av.virtual_class_in_session")
            }
            truncateText
          />
        }
      />
    ) : null;

  const MaybeJoinInPersonClassButton =
    isClassSessionInPerson && !isPracticeClassSession && !isInInPersonClass ? (
      <JoinInPersonClassButton />
    ) : null;

  return (
    <BaseFooter darkMode={isInClassSession} isMobile={isMobile}>
      {isInClassSession && <ReactionFeed />}
      <div
        className={twMerge(
          "flex h-full w-full items-center",
          isInClassSession && !isMobile && "justify-between",
          isInClassSession && isMobile && "justify-center",
          !isInClassSession && "justify-end",
        )}
      >
        <div className="flex items-center pr-2">
          <LazyVideoMicControls isMobile={isMobile} showOptionsMenu={!isMobile} />
          {!isMobile && <LargeClassHandControl />}
        </div>
        <div
          className={twMerge(
            "flex h-full min-w-0 items-center gap-2",
            !isInClassSession ? "justify-end" : "justify-center",
            !isMobile && "w-full",
          )}
        >
          {MaybeJoinVirtualClassButton}
          {MaybeJoinInPersonClassButton}
          {isVirtualClassActive && isInVirtualClass && isBreakoutActive && (
            <LazyBreakoutsBanner isMobile={isMobile} />
          )}
          {isInClassSession && <TimerBanner isMobile={isMobile} />}
          {showPresentationOrScreenshareBanner && isMobile && <MobilePresentationBanner />}

          {showPresentationOrScreenshareBanner && !isMobile && (
            //* Here we animate banner width which naturally pushes buttons
            <div className="flex items-center justify-end gap-2">
              {/* Banner with animated width - content invisible until animation completes */}
              <div
                className={twMerge(
                  "overflow-hidden",
                  // Transition width during animating phases
                  (animationPhase === "animating" || animationPhase === "exitAnimating") &&
                    "transition-[width] duration-700 ease-out",
                )}
                style={{
                  // Use measured width for precise animation
                  width:
                    animationPhase === "offset" || animationPhase === "exitAnimating"
                      ? 0
                      : animationPhase === "animating" || animationPhase === "exitOffset"
                        ? (bannerWidth ?? undefined)
                        : undefined,
                }}
              >
                <div
                  className={twMerge(animationPhase !== "idle" && "invisible")}
                  ref={bannerContentRef}
                >
                  {screensharingClassUserId ? (
                    <ScreenshareOptions autoFollow={isInClassSession} />
                  ) : (
                    <PresentationOptions isInClassSession />
                  )}
                </div>
              </div>
            </div>
          )}

          {isStudentPreview && <StudentPreviewOptions />}
          <IconButtons />
        </div>

        <div className="pl-2">{isInClassSession && <LeaveClassButton isMobile={isMobile} />}</div>
      </div>
    </BaseFooter>
  );
};
